<template>
  <v-row v-if="accountInfo">
    <v-col cols="12">
      <div :style="{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }">
        <ServiceLogo :service="IApp.IFeedTypes.GoogleSearchConsole" size="50px" />
        <h2 class="ml-2 title">Google Search Metrics</h2>
      </div>
    </v-col>

    <!-- Top Queries by Clicks -->
    <v-col cols="12">
      <v-card class="w-full">
        <ChartLazyLoader :loading="state.queryLoading">
          <TopQueriesByClickChart
            :data="topQueriesData"
            :title="`Top Queries by Clicks`"
            :loading="false"
            item="query"
            itemName="Query"
          />
        </ChartLazyLoader>
      </v-card>
    </v-col>
  </v-row>

  <!-- Top Landing Pages by Clicks -->
  <v-row v-if="accountInfo">
    <v-col cols="12">
      <v-card class="w-full">
        <ChartLazyLoader :loading="state.pageLoading">
          <TopQueriesByClickChart
            :data="topPagesData"
            :title="`Top Landing Pages by Clicks`"
            :loading="false"
            item="page"
            itemName="Page"
          />
        </ChartLazyLoader>
      </v-card>
    </v-col>
  </v-row>

  <v-row v-if="accountInfo" class="page-break">
    <v-col cols="12" md="12" lg="12" xl="4">
      <v-card class="w-full">
        <v-card-text>
          <ColumnChart v-bind="impressionChart" />
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12" md="12" lg="12" xl="4">
      <v-card class="w-full">
        <v-card-text>
          <ColumnChart v-bind="ctrsChart" />
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
  <v-row>
    <!-- Ctr by Viewports -->
    <v-col cols="12" md="12" lg="12" xl="4" v-if="accountInfo">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.impressionPositionClickLoading">
            <LineChart v-bind="impressionsLine" class="no-axis-label-in-tooltip" />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Average Position by Month -->
    <v-col cols="12" md="12" lg="12" xl="4" v-if="accountInfo">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.impressionPositionClickLoading">
            <LineChart v-bind="avgPositionLine" class="no-axis-label-in-tooltip" />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Url Clicks by Month -->
    <v-col cols="12" md="12" lg="12" xl="4" v-if="accountInfo">
      <v-card class="w-full">
        <v-card-text>
          <ChartLazyLoader :loading="state.impressionPositionClickLoading">
            <LineChart v-bind="urlClicksLine" class="no-axis-label-in-tooltip" />
          </ChartLazyLoader>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
const props = defineProps<{
  account: IApp.IAccount;
  monthRange: IApp.MonthRange;
  comparedMonthRange: IApp.MonthRange;
  printMode: IApp.PrintMode;
  report?: IApp.IReport | null;
  requestId?: string;
  reportId?: string;
  onChangeSummary?: (summary: string) => void;
  mode?: IApp.PrintMode;
  showSummary: boolean;
}>();

import TopQueriesByClickChart from "@/components/TopQueriesByClickChart.vue";
import { type CtrByViewportsChartProps } from "../CtrByViewportsChart.vue";

import { computed, onMounted, reactive, ref, watch } from "vue";
import { IAnalyticsData, IApp } from "@/types";
import GoogleSearchConsoleAction from "@/actions/GoogleSearchConsole";
import _ from "lodash";
import moment from "moment";
import { formatToShortMonth, getRelativeURL, makeAlert, store } from "@/utils";
import LineChart from "../LineChart.vue";
import ServiceLogo from "../Widgets/ServiceLogo.vue";
import { useAuthStore } from "@/stores/auth";
import ColumnChart, { type ColumnChartProps } from "../Charts/ColumnChart.vue";
import ChartLazyLoader from "@/components/ChartLazyLoader.vue";

const authStore = useAuthStore();
const user = computed(() => authStore.user);

interface ChartData {
  x: any;
  y: any;
  fill?: ApexFill;
  fillColor?: string;
  strokeColor?: string;
  meta?: any;
  goals?: {
    name?: string;
    value: number;
    strokeHeight?: number;
    strokeWidth?: number;
    strokeColor?: string;
    strokeDashArray?: number;
    strokeLineCap?: "butt" | "square" | "round";
  }[];
  barHeightOffset?: number;
  columnWidthOffset?: number;
}

const startMonth = computed(() => {
  return props.monthRange.start;
});
const endMonth = computed(() => props.monthRange.end);
const monthRangeString = computed(
  () => `${formatToShortMonth(startMonth.value)} - ${formatToShortMonth(endMonth.value)}`
);

const comparedStartMonth = computed(() => props.comparedMonthRange.start);
const comparedEndMonth = computed(() => props.comparedMonthRange.end);
const comparedMonthRangeString = computed(
  () =>
    `${formatToShortMonth(comparedStartMonth.value)} - ${formatToShortMonth(comparedEndMonth.value)}`
);
const topQueriesData = computed(() =>
  store(dataTotal, "Top Queries by Clicks", queryAnalyticsData.value)
);
const topPagesData = computed(() =>
  store(dataTotal, "Top Landing Pages by Clicks", pageAnalyticsData.value)
);
const impressionChart = computed(() =>
  store(dataTotal, impressionColumnChartData.value.title, impressionColumnChartData.value)
);
const ctrsChart = computed(() =>
  store(dataTotal, ctrsColumnChartData.value.title, ctrsColumnChartData.value)
);
const impressionsLine = computed(() =>
  store(
    dataTotal,
    impressionsByMonthLineChartData.value.title,
    impressionsByMonthLineChartData.value
  )
);
const avgPositionLine = computed(() =>
  store(
    dataTotal,
    averagePositionByMonthLineChartData.value.title,
    averagePositionByMonthLineChartData.value
  )
);
const urlClicksLine = computed(() =>
  store(dataTotal, urlsClicksByMonthLineChartData.value.title, urlsClicksByMonthLineChartData.value)
);

const queryAnalyticsData = ref<IAnalyticsData.IGoogleSearchConsoleQueryPage[]>([]);
const pageAnalyticsData = ref<IAnalyticsData.IGoogleSearchConsoleQueryPage[]>([]);
const deviceTotalAnalyticsData = ref<
  Pick<CtrByViewportsChartProps, "impressions" | "ctrs" | "labels">
>({
  impressions: [],
  ctrs: [],
  labels: []
});
const impressionPositionClickData = ref<{
  impression: ChartData[];
  position: ChartData[];
  clicks: ChartData[];
}>({
  impression: [],
  position: [],
  clicks: []
});
const comparedImpressionPositionClickData = ref<{
  impression: ChartData[];
  position: ChartData[];
  clicks: ChartData[];
}>({
  impression: [],
  position: [],
  clicks: []
});
const comparedDeviceTotalAnalyticsData = ref<
  Pick<CtrByViewportsChartProps, "impressions" | "ctrs" | "labels">
>({
  impressions: [],
  ctrs: [],
  labels: []
});

const state = ref<{
  queryLoading: boolean;
  pageLoading: boolean;
  deviceLoading: boolean;
  impressionPositionClickLoading: boolean;
  accountInfoLoading: boolean;
  accountInfoLoaded: boolean;
  isDataLoaded: boolean;
  numberLoadingFinished: number;
}>({
  queryLoading: true,
  pageLoading: true,
  deviceLoading: true,
  accountInfoLoading: true,
  accountInfoLoaded: false,
  impressionPositionClickLoading: true,
  isDataLoaded: false,
  numberLoadingFinished: 0
});

const emit = defineEmits<{
  (e: "onChangeData", value: Array<{ key: string; value: [] }>): void;
  (e: "onChangeDataLoad", value: { total: number; loaded: number }): void;
}>();

const dataTotal = reactive<Array<{ key: string; value: [] }>>([]);

watch(
  dataTotal,
  (newDataTotal) => {
    emit("onChangeData", newDataTotal);
  },
  { immediate: true, deep: true }
);

const accountInfo = computed<IApp.IGoogleSearchConsoleAccount>(() => props.account as any);

const impressionPositionClickMonths = computed(() => {
  const months = [];
  for (let i = 0; i < 12; i++) {
    months.push(
      moment()
        .subtract(i + 1, "month")
        .format("MMM")
    );
  }
  return months.reverse();
});

const processIpcData = (
  data: IAnalyticsData.IGoogleSearchConsoleAnalyticsImpressionPositionClick | null,
  target: typeof impressionPositionClickData.value
) => {
  if (!data) return;
  target.clicks = _.map(data, (item) => ({
    y: item.clicks,
    x: moment(item.month, "YYYY-MM").format("MMM YYYY"),
    meta: item
  }));
  target.impression = _.map(data, (item) => ({
    y: item.impressions,
    x: moment(item.month, "YYYY-MM").format("MMM YYYY"),
    meta: item
  }));
  target.position = _.map(data, (item) => ({
    y: item.position / item.impressions,
    x: moment(item.month, "YYYY-MM").format("MMM YYYY"),
    meta: item
  }));
};

const fetchAllData = async () => {
  state.value.queryLoading = true;
  state.value.pageLoading = true;
  state.value.deviceLoading = true;
  state.value.impressionPositionClickLoading = true;

  const response = await GoogleSearchConsoleAction.getAllAnalyticsData(
    user.value?.id!,
    accountInfo.value.customerId,
    accountInfo.value.account,
    {
      monthRange: { start: startMonth.value, end: endMonth.value },
      comparedMonthRange: { start: comparedStartMonth.value, end: comparedEndMonth.value }
    }
  );

  if (response.status) {
    const d = response.data as Record<string, unknown>;

    if (d["query"]) {
      const queryData = d["query"] as Array<IAnalyticsData.IGoogleSearchConsoleQuery>;
      queryAnalyticsData.value = _.map(queryData, (item) => ({
        ...item,
        item: item.query
      }));
    }
    state.value.queryLoading = false;

    if (d["page"]) {
      const pageData = d["page"] as Array<IAnalyticsData.IGoogleSearchConsolePage>;
      pageAnalyticsData.value = _.map(pageData, (item) => ({
        ...item,
        item: getRelativeURL(item.page)
      }));
    }
    state.value.pageLoading = false;

    if (d["device-total"]) {
      const deviceData = d["device-total"] as Array<IAnalyticsData.IGoogleSearchConsoleDeviceTotal>;
      deviceTotalAnalyticsData.value = {
        impressions: _.map(deviceData, (item) => item.curr.impressions),
        ctrs: _.map(deviceData, (item) => Number(item.curr.ctr) * 100),
        labels: _.map(deviceData, (item) => _.capitalize(item.curr.device))
      };
      comparedDeviceTotalAnalyticsData.value = {
        impressions: _.map(deviceData, (item) => item.compared.impressions),
        ctrs: _.map(deviceData, (item) => Number(item.compared.ctr) * 100),
        labels: _.map(deviceData, (item) => _.capitalize(item.compared.device))
      };
    }
    state.value.deviceLoading = false;

    processIpcData(
      d[
        "impression-position-click"
      ] as IAnalyticsData.IGoogleSearchConsoleAnalyticsImpressionPositionClick | null,
      impressionPositionClickData.value
    );
    processIpcData(
      d[
        "impression-position-click-compared"
      ] as IAnalyticsData.IGoogleSearchConsoleAnalyticsImpressionPositionClick | null,
      comparedImpressionPositionClickData.value
    );
    state.value.impressionPositionClickLoading = false;
  } else {
    makeAlert(response.message, "error");
    state.value.queryLoading = false;
    state.value.pageLoading = false;
    state.value.deviceLoading = false;
    state.value.impressionPositionClickLoading = false;
  }
};

const impressionColumnChartData = computed<ColumnChartProps>(() => ({
  series: [
    {
      name: `${comparedMonthRangeString.value}`,
      data: _(comparedDeviceTotalAnalyticsData.value.impressions)
        .map((item, index) => ({
          x: comparedDeviceTotalAnalyticsData.value.labels[index],
          y: item,
          meta: { unit: `` }
        }))
        .value()
    },
    {
      name: `${monthRangeString.value}`,
      data: _(deviceTotalAnalyticsData.value.impressions)
        .map((item, index) => ({
          x: deviceTotalAnalyticsData.value.labels[index],
          y: item,
          meta: { unit: `` }
        }))
        .value()
    }
  ],
  showLabels: true,
  labels: deviceTotalAnalyticsData.value.labels,
  loading: state.value.deviceLoading,
  title: `Impressions`
}));

const ctrsColumnChartData = computed<ColumnChartProps>(() => ({
  series: [
    {
      name: `${comparedMonthRangeString.value}`,
      data: _(comparedDeviceTotalAnalyticsData.value.ctrs)
        .map((item, index) => ({
          x: comparedDeviceTotalAnalyticsData.value.labels[index],
          y: item,
          meta: { unit: `%` }
        }))
        .value()
    },
    {
      name: `${monthRangeString.value}`,
      data: _(deviceTotalAnalyticsData.value.ctrs)
        .map((item, index) => ({
          x: deviceTotalAnalyticsData.value.labels[index],
          y: item,
          meta: { unit: `%` }
        }))
        .value()
    }
  ],
  showLabels: true,
  labels: deviceTotalAnalyticsData.value.labels,
  loading: state.value.deviceLoading,
  title: `CTRs`
}));

const impressionsByMonthLineChartData = computed(() => ({
  xAxis: impressionPositionClickMonths.value,
  series: [
    {
      name: `${comparedMonthRangeString.value}`,
      data: comparedImpressionPositionClickData.value.impression
    },
    {
      name: `${monthRangeString.value}`,
      data: impressionPositionClickData.value.impression
    }
  ],
  loading: state.value.impressionPositionClickLoading,
  title: "Impressions by Month"
}));

const averagePositionByMonthLineChartData = computed(() => ({
  xAxis: impressionPositionClickMonths.value,
  series: [
    {
      name: `${comparedMonthRangeString.value}`,
      data: comparedImpressionPositionClickData.value.position
    },
    {
      name: `${monthRangeString.value}`,
      data: impressionPositionClickData.value.position
    }
  ],
  loading: state.value.impressionPositionClickLoading,
  title: "Average Position by Month"
}));

const urlsClicksByMonthLineChartData = computed(() => ({
  xAxis: impressionPositionClickMonths.value,
  series: [
    {
      name: `${comparedMonthRangeString.value}`,
      data: comparedImpressionPositionClickData.value.clicks
    },
    {
      name: `${monthRangeString.value}`,
      data: impressionPositionClickData.value.clicks
    }
  ],
  loading: state.value.impressionPositionClickLoading,
  title: "Url Clicks by Month"
}));

const reFetch = _.debounce(async () => {
  try {
    state.value.numberLoadingFinished = 0;
    await fetchAllData();
    state.value.numberLoadingFinished = 5;
    emit("onChangeDataLoad", { total: 5, loaded: 5 });
    state.value.isDataLoaded = true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err: unknown) {
    makeAlert("Failed to load Google Search Console analytics data", "error");
  }
}, 500);

watch([startMonth, endMonth, comparedStartMonth, comparedEndMonth], async () => {
  state.value.isDataLoaded = false;
  reFetch();
});

onMounted(async () => {
  reFetch();
});
</script>
